'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCard } from './blog-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { BlogsSkeleton } from './blogs-skeleton';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export const BlogsList = ({ locale }: { locale: string }) => {
  const { t } = useTranslations();

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          orderBy: 'created_at',
          order: 'desc',
          page: pageParam,
          results: 4,
        },
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 md:gap-10 lg:gap-12">
      {blogsQuery.isLoading && <BlogsSkeleton />}
      {blogsQuery.isError && (
        <p className="col-span-1 w-full text-center text-lg font-medium text-foreground sm:col-span-2 sm:text-xl lg:text-2xl">
          {t('text1320')}
        </p>
      )}
      {blogsQuery.isSuccess && (
        <>
          {blogs.length > 0 ? (
            blogs.map((blog: Blog) => <BlogCard blog={blog} key={blog.slug} />)
          ) : (
            <p className="col-span-1 w-full text-center text-lg font-medium text-foreground sm:col-span-2 sm:text-xl lg:text-2xl">
              {t('text752')}
            </p>
          )}
        </>
      )}
      <div
        className={cn('col-span-1 mt-12 flex w-full items-center justify-center sm:col-span-2', {
          hidden: !blogsQuery.hasNextPage,
        })}
      >
        <Button
          onClick={() => blogsQuery.fetchNextPage()}
          isLoading={blogsQuery.isFetchingNextPage}
          variant={'outline'}
        >
          {t('text338')}
        </Button>
      </div>
    </div>
  );
};
